// src/hooks/useTicketFormOptions.js
// -----------------------------------------------------------------------------
// Custom hook managing the cascading options and lookup values for Ticket forms.
// Separates the logic for cascading lookups (Project -> Phase, Environment, Severity, Status)
// and Ticket Type -> Sub-Type from the UI rendering layer.
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import projectService from '../api/projectService';
import ticketTypeService from '../api/ticketTypeService';
import projectEnvironmentService from '../api/projectEnvironmentService';
import ticketSeverityService from '../api/ticketSeverityService';
import ticketStatusService from '../api/ticketStatusService';

export function useTicketFormOptions(selectedRecord) {
  // Lookup options arrays
  const [projects, setProjects] = useState([]);
  const [phases, setPhases] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [allSubTypes, setAllSubTypes] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Cascading dropdown options
  const [filteredPhases, setFilteredPhases] = useState([]);
  const [filteredSubTypes, setFilteredSubTypes] = useState([]);
  const [filteredEnvironments, setFilteredEnvironments] = useState([]);
  const [filteredSeverities, setFilteredSeverities] = useState([]);
  const [filteredStatuses, setFilteredStatuses] = useState([]);

  // Active form data state for cascade updates
  const [currentFormValues, setCurrentFormValues] = useState(null);

  // Load lookup options on mount
  useEffect(() => {
    projectService
      .getAll()
      .then((res) => setProjects(res.data || []))
      .catch(() => {});

    projectService
      .getAllPhases()
      .then((res) => setPhases(res.data || []))
      .catch(() => {});

    ticketTypeService
      .getAll()
      .then((res) => {
        const types = res.data || [];
        setTicketTypes(types);
        const promises = types.map((t) => ticketTypeService.getSubTypes(t.ticketTypeID));
        Promise.all(promises)
          .then((results) => {
            const combined = results.flatMap((subRes, idx) => {
              const typeID = types[idx].ticketTypeID;
              return (subRes.data || []).map((sub) => ({ ...sub, ticketTypeID: typeID }));
            });
            setAllSubTypes(combined);
          })
          .catch(() => {});
      })
      .catch(() => {});

    projectEnvironmentService
      .getAll()
      .then((res) => setEnvironments(res.data || []))
      .catch(() => {});

    ticketSeverityService
      .getAll()
      .then((res) => setSeverities(res.data || []))
      .catch(() => {});

    ticketStatusService
      .getAll()
      .then((res) => setStatuses(res.data || []))
      .catch(() => {});
  }, []);

  // Synchronize cascading filters when selecting a record for editing
  useEffect(() => {
    if (selectedRecord) {
      setCurrentFormValues({
        ...selectedRecord,
        projectID: selectedRecord.projectID || '',
        phaseID: selectedRecord.phaseID || '',
        ticketTypeID: selectedRecord.ticketTypeID || '',
        ticketSubTypeID: selectedRecord.ticketSubTypeID || '',
        severityID: selectedRecord.severityID || '',
        statusID: selectedRecord.statusID || '',
        environmentID: selectedRecord.environmentID || '',
      });

      const projID = Number(selectedRecord.projectID);
      const typeID = Number(selectedRecord.ticketTypeID);
      setFilteredSubTypes(allSubTypes.filter((sub) => Number(sub.ticketTypeID) === typeID));

      if (projID) {
        Promise.all([
          projectService.getPhases(projID),
          projectService.getEnvironments(projID),
          projectService.getSeverities(projID),
          projectService.getStatuses(projID),
        ])
          .then(([phasesRes, envsRes, sevsRes, statsRes]) => {
            setFilteredPhases(phasesRes.data || []);
            setFilteredEnvironments(envsRes.data || []);
            setFilteredSeverities(sevsRes.data || []);
            setFilteredStatuses(statsRes.data || []);
          })
          .catch((err) => console.error('Failed to load edit modal cascades:', err));
      }
    } else {
      setCurrentFormValues({
        projectID: '',
        phaseID: '',
        ticketTypeID: '',
        ticketSubTypeID: '',
        title: '',
        description: '',
        detailedDescription: '',
        severityID: '',
        statusID: '',
        environmentID: '',
      });
      setFilteredPhases([]);
      setFilteredSubTypes([]);
      setFilteredEnvironments([]);
      setFilteredSeverities([]);
      setFilteredStatuses([]);
    }
  }, [selectedRecord, allSubTypes]);

  // Handles dropdown cascades during input changes
  const handleFieldChange = async (fieldName, value, nextFormData) => {
    let updatedValues = { ...nextFormData };

    if (fieldName === 'projectID') {
      const projID = Number(value);
      if (!projID) {
        setFilteredPhases([]);
        setFilteredEnvironments([]);
        setFilteredSeverities([]);
        setFilteredStatuses([]);
        updatedValues.phaseID = '';
        updatedValues.environmentID = '';
        updatedValues.severityID = '';
        updatedValues.statusID = '';
        setCurrentFormValues(updatedValues);
        return;
      }

      try {
        const [phasesRes, envsRes, sevsRes, statsRes] = await Promise.all([
          projectService.getPhases(projID),
          projectService.getEnvironments(projID),
          projectService.getSeverities(projID),
          projectService.getStatuses(projID),
        ]);

        const projPhases = phasesRes.data || [];
        const projEnvs = envsRes.data || [];
        const projSevs = sevsRes.data || [];
        const projStats = statsRes.data || [];

        setFilteredPhases(projPhases);
        setFilteredEnvironments(projEnvs);
        setFilteredSeverities(projSevs);
        setFilteredStatuses(projStats);

        // Default values for other select fields
        if (projPhases.length > 0) {
          updatedValues.phaseID = projPhases[0].phaseID;
        } else {
          updatedValues.phaseID = '';
        }

        // Requirement #27: a project's fixed environment wins (the API applies
        // it too); otherwise default to the first environment.
        const project = projects.find((p) => Number(p.projectID) === projID);
        const fixedEnvID = Number(project?.defaultEnvironmentID) || 0;
        if (fixedEnvID) {
          setFilteredEnvironments(projEnvs.filter((e) => Number(e.environmentID) === fixedEnvID));
          updatedValues.environmentID = fixedEnvID;
        } else if (projEnvs.length > 0) {
          updatedValues.environmentID = projEnvs[0].environmentID;
        } else {
          updatedValues.environmentID = '';
        }

        if (projSevs.length > 0) {
          updatedValues.severityID = projSevs[0].severityID;
        } else {
          updatedValues.severityID = '';
        }

        if (projStats.length > 0) {
          const sorted = [...projStats].sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));
          updatedValues.statusID = sorted[0].statusID;
        } else {
          updatedValues.statusID = '';
        }

        setCurrentFormValues(updatedValues);
        return updatedValues;
      } catch (err) {
        console.error('Failed to load project cascading details:', err);
        return updatedValues;
      }
    }
    if (fieldName === 'ticketTypeID') {
      const typeID = Number(value);
      const projSubs = allSubTypes.filter((sub) => Number(sub.ticketTypeID) === typeID);
      setFilteredSubTypes(projSubs);

      if (projSubs.length > 0) {
        updatedValues.ticketSubTypeID = projSubs[0].subTypeID;
      } else {
        updatedValues.ticketSubTypeID = '';
      }

      setCurrentFormValues(updatedValues);
      return updatedValues;
    }

    return updatedValues;
  };

  return {
    projects,
    phases,
    ticketTypes,
    allSubTypes,
    environments,
    severities,
    statuses,

    filteredPhases,
    filteredSubTypes,
    filteredEnvironments,
    filteredSeverities,
    filteredStatuses,

    currentFormValues,
    setCurrentFormValues,
    handleFieldChange,
  };
}

export default useTicketFormOptions;
